import React, { Component } from 'react';
import styled from 'styled-components';
import { HotkeysTarget2 } from '@blueprintjs/core';
import $journeys from 'app/stores/ai/$journeys';
import $auth from 'app/stores/$auth';
import { Box, Button, ButtonLink, Flex, FlexColumn, Grid, Icon, Tag, Text } from 'core/components';
import { ReactComponent as EmptySearchIcon } from 'app/assets/icons/empty_search.svg';
import { ProductSkeleton } from 'core/components/Skeletons';
import { HotkeyGroup } from 'app/util/constants/hotkeys';
import { SearchSuggestions } from './SearchSuggestions';
import { RESULT_TYPE_MAP } from './results/ResultTypeMap';
import { isSearchingAll, isSearchingAny } from './searchUtils';

const CategoryRow = styled(Flex)`
  justify-content: space-between;
  cursor: pointer;
`;

const resultsLoadingInnerLarge = (
  <Flex flexDirection="column" gap={4}>
    <ProductSkeleton forceDark width="100%" height="60px" />
    <ProductSkeleton forceDark width="100%" height="60px" />
    <ProductSkeleton forceDark width="100%" height="60px" />
    <ProductSkeleton forceDark width="100%" height="60px" />
    <ProductSkeleton forceDark width="100%" height="60px" />
    <ProductSkeleton forceDark width="100%" height="60px" />
  </Flex>
);

const resultsLoadingInnerSmall = (
  <Flex flexDirection="column" gap={2}>
    <ProductSkeleton forceDark width="100%" height="32px" />
    <ProductSkeleton forceDark width="100%" height="32px" />
    <ProductSkeleton forceDark width="100%" height="32px" />
    <ProductSkeleton forceDark width="100%" height="32px" />
    <ProductSkeleton forceDark width="100%" height="32px" />
    <ProductSkeleton forceDark width="100%" height="32px" />
  </Flex>
);

const resultsLoadingSkeleton = (
  <Grid m={3} gridGap="32px" gridRowGap="32px" gridTemplateColumns="1fr 250px">
    {resultsLoadingInnerLarge}
    {resultsLoadingInnerSmall}
  </Grid>
);

/**
 * Handles the journey action for both search results and hotkey selection
 * @param {string} description - The user's prompt/description for the journey
 * @param {function} onSearchClose - Callback to close the search interface
 */
export const handleJourneyAction = (description, onSearchClose) => {
  onSearchClose();
  $journeys.openCopilot({ isHalfScreen: true });
  $journeys.sendMessage(description, { createNewJourney: true });

  $auth.track('Global Search', {
    tab: 'Search Results',
    destination_screen_type: 'kaizen',
    search_term: description
  });
};

class SearchResults extends Component {
  state = {
    selectedIdx: 0
  };

  componentDidMount() {
    this.selectedItemRef = React.createRef();
  }

  get hotkeys() {
    return [
      {
        allowInInput: true,
        preventDefault: true,
        global: true,
        group: HotkeyGroup.SearchOverlay,
        combo: 'up',
        label: 'Select results up',
        onKeyDown: () => {
          const { selectedIdx } = this.state;
          const listLength = this.getActiveList().length;
          this.setState({ selectedIdx: selectedIdx > 0 ? selectedIdx - 1 : listLength - 1 }, () => {
            this.selectedItemRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
          });
        }
      },
      {
        allowInInput: true,
        preventDefault: true,
        global: true,
        group: HotkeyGroup.SearchOverlay,
        combo: 'down',
        label: 'Select results down',
        onKeyDown: () => {
          const { selectedIdx } = this.state;
          const listLength = this.getActiveList().length;
          this.setState({ selectedIdx: selectedIdx < listLength - 1 ? selectedIdx + 1 : 0 }, () => {
            this.selectedItemRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
          });
        }
      },
      {
        allowInInput: true,
        preventDefault: true,
        global: true,
        group: HotkeyGroup.SearchOverlay,
        combo: 'enter',
        label: 'Open selected result',
        onKeyDown: () => {
          const { selectedIdx } = this.state;
          this.handleHotkeySelect(selectedIdx);
        }
      }
    ];
  }

  getActiveList() {
    const { categories, results } = this.props;
    return categories.reduce((acc, curr) => {
      const categoryRes = results.filter((result) => result.category === curr.name);
      if (!curr.active || !curr.filtered) {
        return acc;
      }
      if (!curr.expanded) {
        return acc.concat(categoryRes.slice(0, 5));
      }
      return acc.concat(categoryRes);
    }, []);
  }

  handleHotkeySelect(idx) {
    const { onItemClick, onSearchClose } = this.props;
    const activeList = this.getActiveList();
    const item = activeList[idx];

    if (item) {
      // Handle journey type specially
      if (item.category === RESULT_TYPE_MAP.journey.name) {
        handleJourneyAction(item.description, onSearchClose);
      } else {
        onItemClick(
          { type: item.category, id: item.id || item.value, path: item.path, options: item.options || {} },
          'Search Results'
        );
      }
    }
  }

  handleMouseOverRow(idx) {
    const { selectedIdx } = this.state;
    if (selectedIdx !== idx) {
      this.setState({ selectedIdx: idx });
    }
  }

  componentDidUpdate(prevProps) {
    const { categories, results, rawResults, handleToggleExpandCategory } = this.props;
    if (results !== prevProps.results || categories !== prevProps.categories) {
      const activeCategories = this.getActiveCategories(categories);
      const prevActiveCategories = this.getActiveCategories(prevProps.categories);
      const expandedCategories = this.getExpandedCategories(categories);
      const categorizedResults = this.getCategorizedResults(results, rawResults, activeCategories);
      const prevCategorizedResults = this.getCategorizedResults(
        prevProps.results,
        prevProps.rawResults,
        prevActiveCategories
      );

      const prevKeys = Object.keys(prevCategorizedResults).filter((key) => key !== RESULT_TYPE_MAP.journey.name);
      const currKeys = Object.keys(categorizedResults).filter((key) => key !== RESULT_TYPE_MAP.journey.name);

      if (currKeys.length === 1 && !expandedCategories.has(currKeys[0])) {
        handleToggleExpandCategory(currKeys[0]);
      }
      if (prevKeys.length === 1 && currKeys.length !== 1) {
        handleToggleExpandCategory(prevKeys[0]);
      }
    }
  }

  getActiveCategories(categories) {
    return new Set(categories.filter((cat) => cat.active && cat.filtered).map((cat) => cat.name));
  }

  getExpandedCategories(categories) {
    return new Set(categories.filter((cat) => cat.expanded).map((cat) => cat.name));
  }

  getFilteredResults(results, activeCategories) {
    return results.filter((result) => activeCategories.has(result.category));
  }

  getCategorizedResults(results, rawResults, activeCategories) {
    const categorizedResults = {};
    const filteredResults = this.getFilteredResults(results, activeCategories);

    filteredResults.forEach((res) => {
      if (!categorizedResults[res.category]) {
        categorizedResults[res.category] = {
          category: res.category,
          length: rawResults[res.category]?.length || 0,
          results: []
        };
      }
      categorizedResults[res.category].results.push(res);
    });

    return categorizedResults;
  }

  /**
   *
   * @param {Set<string>} categories
   * @returns {string[]}
   * Ensure 'navigation' category is always at the top of the list for easy Search-based Site navigation
   */
  forceNavCatToSecond(categories) {
    const navigationIndex = categories.findIndex((cat) => cat === RESULT_TYPE_MAP.navigation.name);

    if (navigationIndex > -1 && navigationIndex !== 1) {
      categories.splice(navigationIndex, 1);
      categories.splice(1, 0, RESULT_TYPE_MAP.navigation.name);
    }

    return categories;
  }

  render() {
    const {
      categories,
      results,
      rawResults,
      searched,
      searching,
      onSearchClose,
      onItemClick,
      query,
      handleToggleExpandCategory,
      handleToggleFilteredCategory
    } = this.props;
    const { selectedIdx } = this.state;

    if ((!searched || query === '') && !isSearchingAny(searching)) {
      return <SearchSuggestions />;
    }

    if (isSearchingAll(searching)) {
      return resultsLoadingSkeleton;
    }

    const activeCategories = this.getActiveCategories(categories);
    const filteredResults = this.getFilteredResults(results, activeCategories);
    const categorizedResults = this.getCategorizedResults(results, rawResults, activeCategories);
    const expandedCategories = this.getExpandedCategories(categories);
    const categoryList = this.forceNavCatToSecond(Array.from(activeCategories));

    if (results.length === 0 && !isSearchingAny(searching)) {
      return (
        <HotkeysTarget2 hotkeys={this.hotkeys}>
          <Flex flexDirection="column" alignItems="center" justifyContent="center" pt={5} gap={1}>
            <Text fontSize={24} mb={2} lineHeight="26px">
              Sorry, nothing matched
            </Text>
            <Flex flexDirection="column" alignItems="center" justifyContent="center">
              <Text fontSize={16}>We couldn&apos;t find anything matching that word or phrase.</Text>
              <Text fontSize={16}>Please try something else, or check out the Knowledge Base.</Text>
            </Flex>
            <Icon icon={EmptySearchIcon} iconSize={250} mt={4} />
          </Flex>
        </HotkeysTarget2>
      );
    }

    // Done loading, has results
    return (
      <HotkeysTarget2 hotkeys={this.hotkeys}>
        <Grid gridGap={0} gridTemplateColumns="3fr 1fr" gridRowGap={0} gridColumnGap="4px">
          <FlexColumn p={0} flex={3} overflow="auto" style={{ scrollbarWidth: 'none' }}>
            {categoryList.map((category, idx) => {
              if (!categorizedResults[category]) {
                return null;
              }
              const { length, results: catResults } = categorizedResults[category];

              const offset = categoryList
                .slice(0, idx)
                .reduce(
                  (acc, curr) => acc + Math.min(rawResults[curr]?.length, expandedCategories.has(curr) ? 1000 : 5),
                  0
                );
              const { Component: SearchResult, Icon: IconCmp, hideCategoryHeader } = RESULT_TYPE_MAP[category];

              return (
                <FlexColumn key={category} pb={1}>
                  {!hideCategoryHeader && (
                    <Box py={1} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <Text large>{RESULT_TYPE_MAP[category]?.label}</Text>
                    </Box>
                  )}
                  <Flex flexDirection="column" gap="4px">
                    {catResults.slice(0, expandedCategories.has(category) ? 1000 : 5).map((result, resIdx) => {
                      const isSelectedItem = resIdx + offset === selectedIdx;
                      return (
                        <div
                          ref={isSelectedItem ? this.selectedItemRef : null}
                          className={`search-result-item-${result.category}`}
                          key={`${result.category}.${result.id || result.path}`}
                          style={{ cursor: 'pointer' }}
                        >
                          <SearchResult
                            item={result}
                            onItemClick={onItemClick}
                            onSearchClose={onSearchClose}
                            selected={isSelectedItem}
                            IconCmp={IconCmp}
                            // use onMouseMove instead of onMouseEnter/onMouseOver to avoid triggering on scroll
                            onMouseMove={() => this.handleMouseOverRow(resIdx + offset)}
                          />
                        </div>
                      );
                    })}
                  </Flex>
                  <Flex justifyContent="flex-end" height="16px">
                    {length > 5 && !expandedCategories.has(category) && Object.keys(categorizedResults).length > 1 && (
                      <Button borderRadius="15px" minimal onClick={() => handleToggleExpandCategory(category)}>
                        {`+ ${length - 5} more`}
                      </Button>
                    )}
                  </Flex>
                </FlexColumn>
              );
            })}
            {!isSearchingAny(searching) && filteredResults.length === 0 && (
              <Box textAlign="center" p={2}>
                <Text muted fontSize={18}>
                  No results matching selected categories
                </Text>
              </Box>
            )}

            {/* If any type of search query is still pending, signal that to user w/ loading skeleton */}
            {isSearchingAny(searching) && resultsLoadingInnerLarge}
          </FlexColumn>
          <Box p={2}>
            {/* If any search is still pending, show loading skeleton instead of categories sidebar */}
            {isSearchingAny(searching) ? (
              resultsLoadingInnerSmall
            ) : (
              <>
                <Flex flexDirection="column" flex={1}>
                  {Array.from(categories)
                    .filter((cat) => cat.name !== RESULT_TYPE_MAP.journey.name)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((category) => {
                      const { filtered, name } = category;
                      const numResults = rawResults && rawResults[name] && rawResults[name].length;

                      if (!numResults || !name || !RESULT_TYPE_MAP[name]) {
                        return null;
                      }

                      const { label, Icon: CategoryIcon } = RESULT_TYPE_MAP[name];

                      return (
                        <CategoryRow
                          key={name}
                          py={1}
                          px="4px"
                          onClick={() => handleToggleFilteredCategory(name)}
                          borderRadius="4px"
                        >
                          <Flex alignItems="center">
                            <CategoryIcon color={filtered ? '#fff' : 'muted'} />
                            <Text
                              fontWeight="bold"
                              mx={1}
                              muted={!filtered}
                              ellipsis
                              overflow="hidden"
                              whiteSpace="nowrap"
                              width="100%"
                            >
                              {label || name}
                            </Text>
                          </Flex>
                          <Tag intent={filtered ? 'primary' : 'none'} color="#fff" minimal={!filtered} round>
                            {numResults}
                          </Tag>
                        </CategoryRow>
                      );
                    })}
                </Flex>
                <Box textAlign="right">
                  <ButtonLink small minimal onClick={() => handleToggleFilteredCategory('all')}>
                    All
                  </ButtonLink>
                  <Text fontSize="small" mx={1}>
                    |
                  </Text>
                  <ButtonLink small minimal onClick={() => handleToggleFilteredCategory('none')}>
                    None
                  </ButtonLink>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </HotkeysTarget2>
    );
  }
}

export default SearchResults;
